import { CalendarEventType, EventCalendar } from '@/types/Calendar.ts'
import { OccasionalSlot } from '@/types/Slots.ts'
import { StaffLevelRights, StaffWithLinks } from '@/types/Staff.ts'
import { PlanningRights, PlanningTabId } from '@/hooks/usePlanningRights.ts'

export function getFilteredEvents(
    planningActiveTab: PlanningTabId,
    planningRights: PlanningRights,
    events: EventCalendar[],
    filters: string[]
): EventCalendar[] {
    let result: EventCalendar[] = getFilteredEventsByTab(planningActiveTab, planningRights, events)

    const childsIds: number[] = filters
        .filter((filter: string): boolean => filter.startsWith('CHILD_'))
        .map((filter: string) => parseInt(filter.split('_')[1]))

    if (childsIds.length > 0) {
        const childsIdsSet: Set<number> = new Set(childsIds)
        result = result.filter((event: EventCalendar): boolean => {
            if (childsIdsSet.has(Number(event.childId))) {
                return true
            }
            if (event.type === CalendarEventType.AVAILABLE) {
                const slots: OccasionalSlot[] = event.slots as OccasionalSlot[]
                return slots.some((slot: OccasionalSlot): boolean => {
                    return slot.matching.some((match: number): boolean => {
                        return childsIdsSet.has(Number(match))
                    })
                })
            }
            return false
        })
    }

    const sectionsIds: number[] = filters
        .filter((filter: string): boolean => filter.startsWith('SECTION_'))
        .map((filter: string) => parseInt(filter.split('_')[1]))

    if (sectionsIds.length > 0) {
        const res: EventCalendar[] = result.filter((event: EventCalendar): boolean => {
            if (
                event.type === CalendarEventType.REGULAR ||
                event.type === CalendarEventType.ABSENCE ||
                event.type === CalendarEventType.REGULAR_REQUEST
            ) {
                return sectionsIds.includes(Number(event.sectionId))
            }
            return false
        })
        result =
            res.length > 0
                ? res
                : result.filter((event: EventCalendar): boolean => sectionsIds.includes(Number(event.sectionId)))
    }

    const placesIds: number[] = filters
        .filter((filter: string): boolean => filter.startsWith('PLACE_'))
        .map((filter: string) => parseInt(filter.split('_')[1]))

    if (placesIds.length > 0) {
        result = result.filter((event: EventCalendar): boolean => placesIds.includes(Number(event.placeId)))
    }

    const levelsIds: number[] = filters
        .filter((filter: string): boolean => filter.startsWith('LEVEL_'))
        .map((filter: string) => parseInt(filter.split('_')[1]))

    if (levelsIds.length > 0) {
        result = result.filter((event: EventCalendar): boolean => levelsIds.includes(Number(event.levelId)))
    }

    const staffRatesIds: number[] = filters
        .filter((filter: string): boolean => filter.startsWith('RATE_'))
        .map((filter: string) => parseInt(filter.split('_')[1]))

    if (staffRatesIds.length > 0) {
        result = result.filter((event: EventCalendar): boolean => staffRatesIds.includes(Number(event.sectionGroupId)))
    }

    const types: CalendarEventType[] = filters
        .filter(
            (filter: string): boolean =>
                !filter.startsWith('CHILD_') &&
                !filter.startsWith('SECTION_') &&
                !filter.startsWith('PLACE_') &&
                !filter.startsWith('LEVEL_') &&
                !filter.startsWith('RATE_')
        )
        .map((filter: string) => filter as CalendarEventType)

    const filterTypes: EventCalendar[] = []

    if (types.length > 0) {
        types.map((type: CalendarEventType) => {
            switch (type) {
                case CalendarEventType.AVAILABLE:
                    filterTypes.push(
                        ...result.filter(
                            (event: EventCalendar): boolean =>
                                event.type === CalendarEventType.AVAILABLE ||
                                event.type === CalendarEventType.REMAINDER_AVAILABLE ||
                                event.type === CalendarEventType.EXPIRED
                        )
                    )
                    break
                case CalendarEventType.REGULAR:
                    filterTypes.push(
                        ...result.filter((event: EventCalendar): boolean => event.type === CalendarEventType.REGULAR)
                    )
                    break
                default:
                    filterTypes.push(...result.filter((event: EventCalendar): boolean => event.type === type))
            }
        })
    } else {
        filterTypes.push(...result)
    }

    return filterTypes
}

function getFilteredEventsByTab(
    planningActiveTab: PlanningTabId,
    planningRights: PlanningRights,
    events: EventCalendar[]
): EventCalendar[] {
    switch (planningActiveTab) {
        case PlanningTabId.REGULAR_TAB:
            return planningRights.canReadContracts
                ? events.filter((event: EventCalendar): boolean => {
                      return (
                          event.type === CalendarEventType.REGULAR ||
                          event.type === CalendarEventType.ABSENCE ||
                          event.type === CalendarEventType.REGULAR_REQUEST ||
                          event.type === CalendarEventType.CLOSING_DAY
                      )
                  })
                : []
        case PlanningTabId.OCCASIONAL_TAB:
            return planningRights.canReadOccasionalSlot
                ? events.filter((event: EventCalendar): boolean => {
                      return (
                          event.type === CalendarEventType.AVAILABLE ||
                          event.type === CalendarEventType.SCHEDULED ||
                          event.type === CalendarEventType.REQUEST ||
                          event.type === CalendarEventType.VALIDATED ||
                          event.type === CalendarEventType.REMAINDER_AVAILABLE ||
                          event.type === CalendarEventType.CLOSING_DAY ||
                          event.type === CalendarEventType.EVENT ||
                          event.type === CalendarEventType.EXPIRED
                      )
                  })
                : []

        case PlanningTabId.STAFF_TAB:
            return planningRights.canReadStaffWorks || planningRights.isStaff
                ? events.filter((event: EventCalendar): boolean => {
                      return (
                          event.type === CalendarEventType.STAFF_HOLIDAY ||
                          event.type === CalendarEventType.STAFF_TRAINING ||
                          event.type === CalendarEventType.IN_SECTION ||
                          event.type === CalendarEventType.NOT_IN_SECTION ||
                          event.type === CalendarEventType.CLOSING_DAY ||
                          event.type === CalendarEventType.EVENT
                      )
                  })
                : []
        case PlanningTabId.GLOBAL_TAB:
            return planningRights.canViewGlobalTab
                ? events.filter((event: EventCalendar): boolean => {
                      return event.type === CalendarEventType.RATE_SLOT || event.type === CalendarEventType.CLOSING_DAY
                  })
                : []
        default:
            return events
    }
}

export function getSortedStaffs(staffs: StaffWithLinks[], filters: string[], daycareId: number): StaffWithLinks[] {
    let result: StaffWithLinks[] = staffs

    const levelsIds: number[] = filters
        .filter((filter: string): boolean => filter.startsWith('LEVEL_'))
        .map((filter: string): number => parseInt(filter.split('_')[1]))

    if (levelsIds.length > 0) {
        result = result.filter((staff: StaffWithLinks): boolean => {
            const links: StaffLevelRights[] = staff.staffLinks.filter(
                (link: StaffLevelRights): boolean => link.daycareId === daycareId
            )
            return links.some((link: StaffLevelRights): boolean => levelsIds.includes(link.level))
        })
    }

    return result
}
